/**
 * Deterministische kleur/dim-overlay op een diagram (§ "Deterministische
 * diagram-vereenvoudiging" in 05-backlog.md — conditional-formatting-tak,
 * geïnspireerd op BiZZdesign Enterprise Studio's kleur-op-logica-views).
 *
 * Een overlay is een **regelset**: elke regel koppelt een `condition`
 * (type/subtype + stereotype + laag + attribuut-vergelijkingen + relatie-
 * bestaan) aan een `effect` (vulkleur / randkleur / tekstkleur / dim). De
 * engine (`evaluateOverlay`) is een pure functie: gelijke input → gelijke
 * output, geen randomness, geen I/O. De LLM-laag (later) schrijft alleen
 * regel-JSON in dit schema; het toepassen blijft deterministisch en
 * audit-baar.
 *
 * Toepassen is niet-destructief: de overlay leeft als config op de diagram-
 * viewState en overschrijft alleen de render-kleur van concepten die een
 * regel matchen. "Wis overlay" (of `enabled: false`) → terug naar de
 * standaard-notatiekleuren. Geen undo nodig.
 *
 * Subtype-bewust: `condition.typeIds` en `relation.otherTypeIds` matchen op
 * de *effectieve* type-ids (een subtype telt mee via z'n parent-keten). De
 * web-laag levert die effectieve set aan via `RegistryRuntime`.
 */
import type { BilingualText } from "./types";

export type OverlayComparator =
  | "eq"
  | "neq"
  | "lt"
  | "lte"
  | "gt"
  | "gte"
  | "contains"
  | "exists"
  | "not_exists"
  | "in";

/** Eén attribuut-vergelijking. `value` ontbreekt bij exists/not_exists. */
export interface OverlayAttributeClause {
  attribute: string;
  comparator: OverlayComparator;
  value?: string | number | readonly string[];
}

/** Relatie-bestaan-clausule: matcht als het concept zo'n relatie heeft. */
export interface OverlayRelationClause {
  relationTypeIds?: readonly string[];
  direction?: "outgoing" | "incoming" | "any";
  /** Effectieve type-ids van de andere kant (subtype-bewust). */
  otherTypeIds?: readonly string[];
  /**
   * Naam van het object aan de andere kant: exact, hoofdletterongevoelig,
   * in een van beide talen ("alles met een relatie met Partij").
   */
  otherName?: string;
}

/**
 * Child-bestaan-clausule: matcht als het concept minstens één child (via
 * `parent_id`) heeft dat aan álle opgegeven facetten voldoet. Gebruikt om een
 * Concept te kleuren op basis van z'n Attribuut/Identifier-children — bv.
 * "heeft een child dat naar attribuuttype «klantnummer» verwijst"
 * (`attribute_type_ref = <id>`).
 */
export interface OverlayChildClause {
  /** Effectieve type-ids van de child (subtype-bewust), bv. sharp.Attribute. */
  typeIds?: readonly string[];
  stereotypes?: readonly string[];
  /** Attribuut-clausules op de child (alle moeten gelden — AND). */
  attributes?: readonly OverlayAttributeClause[];
}

/**
 * Alle opgegeven facetten moeten gelden (AND). Een weggelaten facet legt
 * geen beperking op; een volledig lege condition matcht elk concept
 * (bruikbaar als catch-all, bv. "dim alles, kleur daarna specifieke types").
 */
export interface OverlayCondition {
  /** Effectieve type-ids moeten overlappen (subtype-bewust). */
  typeIds?: readonly string[];
  /** Concept draagt minstens één van deze stereotypes. */
  stereotypes?: readonly string[];
  /** Laag van het concept staat in deze lijst. */
  layers?: readonly string[];
  /** Attribuut-clausules (alle moeten gelden — AND). */
  attributes?: readonly OverlayAttributeClause[];
  /** Relatie-bestaan-clausule. */
  relation?: OverlayRelationClause;
  /** Child-bestaan-clausule (Attribuut/Identifier via `parent_id`). */
  child?: OverlayChildClause;
}

export interface OverlayEffect {
  /** Vulkleur (hex). */
  fill?: string;
  /** Randkleur (hex). */
  stroke?: string;
  /** Tekstkleur (hex). */
  text?: string;
  /** Gedimd renderen (verlaagde opacity) — context behouden zonder ruis. */
  dim?: boolean;
}

export interface OverlayRule {
  id: string;
  /** Mensvriendelijke omschrijving, bv. "Uitgefaseerde applicaties". */
  label?: BilingualText;
  condition: OverlayCondition;
  effect: OverlayEffect;
  /** Default true; op false laat de regel de regelset intact maar inactief. */
  enabled?: boolean;
}

export interface OverlayRuleSet {
  /** Master-schakelaar: false = overlay uit (standaard-notatiekleuren). */
  enabled: boolean;
  /**
   * `first-match` (default): het effect van de eerste matchende regel wint.
   * `last-match`: het effect van de laatste matchende regel wint. Zo kan de
   * modelleur volgorde gebruiken om specifiek boven generiek te leggen.
   */
  strategy?: "first-match" | "last-match";
  rules: OverlayRule[];
}

/** Per-concept input voor de engine — door de web-laag samengesteld. */
export interface OverlayConceptInput {
  typeId: string;
  /** typeId + extension-parent-keten (subtype IS-EEN parent). */
  effectiveTypeIds: readonly string[];
  layer: string;
  stereotypes: readonly string[];
  attributes: Record<string, unknown>;
  /**
   * De eigen naam van het object (tweetalig of platte tekst). Een
   * attribuut-clausule op `name` vergelijkt hiermee, tenzij het object een
   * echt attribuut `name` heeft; dat gaat voor.
   */
  name?: unknown;
  /** Relaties die dit concept raken (voor relatie-clausules). */
  relations: readonly OverlayRelationInput[];
  /** Directe children (via `parent_id`) — voor child-clausules. */
  children: readonly OverlayChildInput[];
}

export interface OverlayRelationInput {
  relationTypeId: string;
  direction: "outgoing" | "incoming";
  /** Effectieve type-ids van de andere kant (subtype-bewust). */
  otherEffectiveTypeIds: readonly string[];
  /** Naam van de andere kant (tweetalig of platte tekst). */
  otherName?: unknown;
}

export interface OverlayChildInput {
  effectiveTypeIds: readonly string[];
  stereotypes: readonly string[];
  attributes: Record<string, unknown>;
}

/**
 * Evalueer de overlay voor één concept. Returnt het effect van de winnende
 * regel, of `null` als de overlay uit staat of geen regel matcht (→ concept
 * rendert in z'n standaardkleur).
 */
export function evaluateOverlay(
  input: OverlayConceptInput,
  ruleset: OverlayRuleSet | null | undefined,
): OverlayEffect | null {
  if (!ruleset?.enabled) return null;
  const active = ruleset.rules.filter((r) => r.enabled !== false);
  // last-match: laatste-matchende wint → omgekeerd itereren en eerste-hit pakken.
  const ordered = ruleset.strategy === "last-match" ? [...active].reverse() : active;
  for (const rule of ordered) {
    if (matchesCondition(rule.condition, input)) return rule.effect;
  }
  return null;
}

function matchesCondition(cond: OverlayCondition, input: OverlayConceptInput): boolean {
  if (cond.typeIds && cond.typeIds.length > 0) {
    if (!cond.typeIds.some((t) => input.effectiveTypeIds.includes(t))) return false;
  }
  if (cond.stereotypes && cond.stereotypes.length > 0) {
    if (!cond.stereotypes.some((s) => input.stereotypes.includes(s))) return false;
  }
  if (cond.layers && cond.layers.length > 0) {
    if (!cond.layers.includes(input.layer)) return false;
  }
  if (cond.attributes && cond.attributes.length > 0) {
    const attributes =
      input.name !== undefined && !("name" in input.attributes)
        ? { ...input.attributes, name: input.name }
        : input.attributes;
    for (const clause of cond.attributes) {
      if (!matchesAttribute(clause, attributes)) return false;
    }
  }
  if (cond.relation) {
    if (!matchesRelation(cond.relation, input.relations)) return false;
  }
  if (cond.child) {
    const clause = cond.child;
    if (!input.children.some((c) => matchesChild(clause, c))) return false;
  }
  return true;
}

function matchesChild(clause: OverlayChildClause, child: OverlayChildInput): boolean {
  if (clause.typeIds && clause.typeIds.length > 0) {
    if (!clause.typeIds.some((t) => child.effectiveTypeIds.includes(t))) return false;
  }
  if (clause.stereotypes && clause.stereotypes.length > 0) {
    if (!clause.stereotypes.some((s) => child.stereotypes.includes(s))) return false;
  }
  if (clause.attributes) {
    for (const a of clause.attributes) {
      if (!matchesAttribute(a, child.attributes)) return false;
    }
  }
  return true;
}

/**
 * Vlakt een attribuut-waarde uit naar losse strings. Dekt plain string/
 * number/boolean, arrays, en bilingual/object-bags (neemt de string-bladeren).
 * Zo matcht een attribuut-clausule ongeacht of de waarde eentalig, meertalig
 * of een lijst is.
 */
function attrToStrings(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (typeof value === "string") return [value];
  if (typeof value === "number" || typeof value === "boolean") return [String(value)];
  if (Array.isArray(value)) return value.flatMap(attrToStrings);
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(attrToStrings);
  }
  return [];
}

function scalarStr(value: string | number | readonly string[] | undefined): string {
  if (value === undefined) return "";
  if (Array.isArray(value)) return String(value[0] ?? "").toLowerCase();
  return String(value as string | number).toLowerCase();
}

function arrVals(value: string | number | readonly string[] | undefined): string[] {
  if (value === undefined) return [];
  const arr = Array.isArray(value) ? value : [value as string | number];
  return arr.map((v) => String(v).toLowerCase());
}

/**
 * Zet een string om in een ordenbaar getal voor `<`/`≤`/`>`/`≥`. Eerst als
 * getal; anders als datum via `Date.parse` (ISO `YYYY-MM-DD`, ook met tijd).
 * Zo werken de ordening-vergelijkers op zowel numerieke als datum-attributen.
 * Leeg/onparseerbaar → `null` (geen match). Bron- en clausule-waarde lopen
 * door dezelfde omzetting zodat ze op dezelfde schaal vergeleken worden.
 */
function toComparable(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isNaN(n)) return n;
  const d = Date.parse(trimmed);
  return Number.isNaN(d) ? null : d;
}

function firstComparable(value: unknown): number | null {
  for (const s of attrToStrings(value)) {
    const c = toComparable(s);
    if (c !== null) return c;
  }
  return null;
}

function clauseComparable(value: string | number | readonly string[] | undefined): number | null {
  if (value === undefined) return null;
  const raw = Array.isArray(value) ? value[0] : (value as string | number);
  return toComparable(String(raw));
}

function matchesAttribute(
  clause: OverlayAttributeClause,
  attributes: Record<string, unknown>,
): boolean {
  const raw = attributes[clause.attribute];
  const present = attrToStrings(raw).some((s) => s.trim().length > 0);
  if (clause.comparator === "exists") return present;
  if (clause.comparator === "not_exists") return !present;

  const strs = attrToStrings(raw).map((s) => s.toLowerCase());
  switch (clause.comparator) {
    case "eq":
      return strs.some((s) => s === scalarStr(clause.value));
    case "neq":
      // Logische negatie van eq: afwezig telt als "niet gelijk".
      return !strs.some((s) => s === scalarStr(clause.value));
    case "contains":
      return strs.some((s) => s.includes(scalarStr(clause.value)));
    case "in": {
      const set = arrVals(clause.value);
      return strs.some((s) => set.includes(s));
    }
    case "lt":
    case "lte":
    case "gt":
    case "gte": {
      const a = firstComparable(raw);
      const b = clauseComparable(clause.value);
      if (a === null || b === null) return false;
      return clause.comparator === "lt"
        ? a < b
        : clause.comparator === "lte"
          ? a <= b
          : clause.comparator === "gt"
            ? a > b
            : a >= b;
    }
    default:
      return false;
  }
}

function matchesRelation(
  clause: OverlayRelationClause,
  relations: readonly OverlayRelationInput[],
): boolean {
  return relations.some((r) => {
    if (clause.direction && clause.direction !== "any" && r.direction !== clause.direction) {
      return false;
    }
    if (
      clause.relationTypeIds &&
      clause.relationTypeIds.length > 0 &&
      !clause.relationTypeIds.includes(r.relationTypeId)
    ) {
      return false;
    }
    if (
      clause.otherTypeIds &&
      clause.otherTypeIds.length > 0 &&
      !clause.otherTypeIds.some((t) => r.otherEffectiveTypeIds.includes(t))
    ) {
      return false;
    }
    if (clause.otherName?.trim()) {
      const wanted = clause.otherName.trim().toLowerCase();
      if (!attrToStrings(r.otherName).some((n) => n.trim().toLowerCase() === wanted)) {
        return false;
      }
    }
    return true;
  });
}

/** Reserved viewState-key waaronder de overlay-regelset wordt gestasht. */
export const OVERLAY_RULES_KEY = "__overlay_rules";
